import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import JobListCard from '../../components/mobile/JobListCard';
import PageHeader from '../../components/mobile/PageHeader';
import { useIsMobile } from '../../hooks/useIsMobile';
import { parseDateOnlyLocal, formatDateOnlyForDisplay } from '../../utils/dateOnly';
import {
  addLegacyGallonPriceAliases,
  normalizePricingRow,
  roundPricePerLiterForApi,
} from '../../utils/pricing';

const ManagerDashboard = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    activeDrivers: 0,
    activeJobs: 0,
    completedJobs: 0,
    pendingInvoices: 0,
    totalRevenue: 0,
    totalVehicles: 0,
  });
  const [pricing, setPricing] = useState([]);
  const [filteredPricing, setFilteredPricing] = useState([]);
  const [currentPricing, setCurrentPricing] = useState(null);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingFormData, setPricingFormData] = useState({
    date: '',
    diesel_price_per_liter: '',
    def_price_per_liter: '',
    dyed_price_per_liter: '',
  });
  const [pricingError, setPricingError] = useState('');
  const [pricingSuccess, setPricingSuccess] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [showPastOnly, setShowPastOnly] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchPricing();
    fetchCurrentPricing();
  }, []);

  const fetchDashboard = async () => {
    try {
      setError('');
      const response = await api.get('/managers/dashboard/');
      // Map the response to match expected field names
      setStats({
        activeDrivers: response.data.active_drivers || 0,
        activeJobs: response.data.active_jobs || 0,
        completedJobs: response.data.completed_jobs || 0,
        pendingInvoices: response.data.pending_invoices || 0,
        totalRevenue: response.data.total_revenue || 0,
        totalVehicles: response.data.total_vehicles || 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to load dashboard data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await api.get('/pricing/pricing/');
      const pricingData = (Array.isArray(response.data) ? response.data : []).map(normalizePricingRow);
      setPricing(pricingData);
      setFilteredPricing(pricingData);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const filterPricing = () => {
    let filtered = [...pricing];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter by date search
    if (searchDate) {
      const searchDateObj = parseDateOnlyLocal(searchDate);
      const searchTs = searchDateObj ? searchDateObj.getTime() : null;
      filtered = filtered.filter((price) => {
        const priceDate = parseDateOnlyLocal(price.date);
        return priceDate && searchTs != null && priceDate.getTime() === searchTs;
      });
    }

    // Filter by past only
    if (showPastOnly) {
      filtered = filtered.filter((price) => {
        const priceDate = parseDateOnlyLocal(price.date);
        return priceDate && priceDate < today;
      });
    }

    setFilteredPricing(filtered);
  };

  useEffect(() => {
    filterPricing();
  }, [searchDate, showPastOnly, pricing]);

  const fetchCurrentPricing = async () => {
    try {
      const response = await api.get('/pricing/pricing/current/');
      setCurrentPricing(normalizePricingRow(response.data));
    } catch (error) {
      // Current pricing might not exist yet
      console.log('No current pricing found');
    }
  };

  const handleOpenPricingDialog = (pricingItem = null) => {
    if (pricingItem) {
      const p = normalizePricingRow(pricingItem);
      setPricingFormData({
        id: p.id,
        date: p.date,
        diesel_price_per_liter: p.diesel_price_per_liter ?? '',
        def_price_per_liter: p.def_price_per_liter ?? '',
        dyed_price_per_liter: p.dyed_price_per_liter ?? '',
      });
    } else {
      // Set today's date as default
      const today = new Date().toISOString().split('T')[0];
      setPricingFormData({
        id: null,
        date: today,
        diesel_price_per_liter: '',
        def_price_per_liter: '',
        dyed_price_per_liter: '',
      });
    }
    setPricingDialogOpen(true);
    setPricingError('');
    setPricingSuccess('');
  };

  const handleClosePricingDialog = () => {
    setPricingDialogOpen(false);
    setPricingFormData({
      id: null,
      date: '',
      diesel_price_per_liter: '',
      def_price_per_liter: '',
      dyed_price_per_liter: '',
    });
    setPricingError('');
    setPricingSuccess('');
  };

  const handlePricingChange = (e) => {
    const { name, value } = e.target;
    setPricingFormData({
      ...pricingFormData,
      [name]: value,
    });
  };

  const handlePricingSubmit = async () => {
    setPricingError('');
    setPricingSuccess('');

    if (!pricingFormData.date || !pricingFormData.diesel_price_per_liter || !pricingFormData.def_price_per_liter || pricingFormData.dyed_price_per_liter === '') {
      setPricingError('Please fill in all fields');
      return;
    }

    const dieselPrice = parseFloat(pricingFormData.diesel_price_per_liter);
    const defPrice = parseFloat(pricingFormData.def_price_per_liter);
    const dyedPrice = parseFloat(pricingFormData.dyed_price_per_liter);
    if (!Number.isFinite(dieselPrice) || !Number.isFinite(defPrice) || !Number.isFinite(dyedPrice) || dieselPrice <= 0 || defPrice <= 0 || dyedPrice < 0) {
      setPricingError('Enter valid diesel and DEF prices (greater than 0) and dyed price (0 or greater).');
      return;
    }

    try {
      const payload = addLegacyGallonPriceAliases({
        date: pricingFormData.date,
        diesel_price_per_liter: roundPricePerLiterForApi(dieselPrice),
        def_price_per_liter: roundPricePerLiterForApi(defPrice),
        dyed_price_per_liter: roundPricePerLiterForApi(dyedPrice),
      });

      // Check if we're editing an existing pricing (has ID in formData)
      const editingPricing = pricingFormData.id;
      
      if (editingPricing) {
        // Update existing pricing
        await api.patch(`/pricing/pricing/${editingPricing}/`, payload);
        setPricingSuccess('Pricing updated successfully');
      } else {
        // Check if pricing for this date already exists
        const existingPricing = pricing.find(p => p.date === pricingFormData.date);
        
        if (existingPricing && existingPricing.id) {
          // Update existing pricing for this date
          await api.patch(`/pricing/pricing/${existingPricing.id}/`, payload);
          setPricingSuccess('Pricing updated successfully');
        } else {
          // Create new pricing
          await api.post('/pricing/pricing/', payload);
          setPricingSuccess('Pricing created successfully');
        }
      }

      fetchPricing();
      fetchCurrentPricing();
      setTimeout(() => {
        handleClosePricingDialog();
      }, 1500);
    } catch (error) {
      console.error('Error saving pricing:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to save pricing';
      
      if (errorData) {
        if (errorData.date && Array.isArray(errorData.date)) {
          errorMessage = errorData.date[0];
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      setPricingError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Manager Dashboard
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Drivers
                </Typography>
                <Typography variant="h4">{stats.activeDrivers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Jobs
                </Typography>
                <Typography variant="h4">{stats.activeJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed Jobs
                </Typography>
                <Typography variant="h4">{stats.completedJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Invoices
                </Typography>
                <Typography variant="h4">{stats.pendingInvoices}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4">${stats.totalRevenue.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Vehicles
                </Typography>
                <Typography variant="h4">{stats.totalVehicles}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Pricing Management Section */}
        <Box sx={{ mt: 4 }}>
          {isMobile ? (
            <PageHeader
              title="Pricing Management"
              action={(
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenPricingDialog()}
                  sx={{ minHeight: 44, width: '100%' }}
                >
                  Set Pricing
                </Button>
              )}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Pricing Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenPricingDialog()}
              >
                Set Pricing
              </Button>
            </Box>
          )}

          {/* Search and Filter Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Search by Date"
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant={showPastOnly ? 'contained' : 'outlined'}
              onClick={() => setShowPastOnly(!showPastOnly)}
              size="small"
            >
              {showPastOnly ? 'Show All' : 'Show Past Only'}
            </Button>
            {(searchDate || showPastOnly) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchDate('');
                  setShowPastOnly(false);
                }}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Current Pricing Card */}
          {currentPricing && (
            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Pricing (Today)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1">
                      <strong>Diesel:</strong> ${currentPricing.diesel_price_per_liter}/gal
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1">
                      <strong>DEF:</strong> ${currentPricing.def_price_per_liter}/gal
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1">
                      <strong>Dyed:</strong> ${currentPricing.dyed_price_per_liter}/gal
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Pricing History */}
          {isMobile ? (
            <Box sx={{ maxWidth: 520, mx: 'auto' }}>
              {filteredPricing.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {pricing.length === 0
                    ? 'No pricing records found. Tap Set Pricing to add pricing.'
                    : 'No pricing records match your filters.'}
                </Typography>
              ) : (
                filteredPricing.map((price) => {
                  const priceDate = parseDateOnlyLocal(price.date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isPast = priceDate && priceDate < today;
                  const isToday = priceDate && priceDate.getTime() === today.getTime();
                  const statusLabel = isPast ? 'Past' : isToday ? 'Current' : 'Future';
                  const statusColor = isPast ? 'default' : isToday ? 'primary' : 'success';

                  return (
                    <JobListCard
                      key={price.id}
                      title={formatDateOnlyForDisplay(price.date)}
                      chips={[
                        <Chip key="status" label={statusLabel} size="small" color={statusColor} />,
                      ]}
                      actions={(
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenPricingDialog(price)}
                          sx={{ minHeight: 44 }}
                        >
                          Edit
                        </Button>
                      )}
                      fields={[
                        { label: 'Diesel $/gal', value: `$${price.diesel_price_per_liter}` },
                        { label: 'DEF $/gal', value: `$${price.def_price_per_liter}` },
                        { label: 'Dyed $/gal', value: `$${price.dyed_price_per_liter ?? '0'}` },
                        { label: 'Created by', value: price.created_by_name || 'N/A', fullWidth: true },
                      ]}
                    />
                  );
                })
              )}
            </Box>
          ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Diesel $/gal</TableCell>
                  <TableCell>DEF $/gal</TableCell>
                  <TableCell>Dyed $/gal</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPricing.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {pricing.length === 0 
                          ? 'No pricing records found. Click "Set Pricing" to add pricing.'
                          : 'No pricing records match your filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPricing.map((price) => {
                    const priceDate = parseDateOnlyLocal(price.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isPast = priceDate && priceDate < today;
                    const isToday = priceDate && priceDate.getTime() === today.getTime();
                    const isFuture = priceDate && priceDate > today;

                    return (
                      <TableRow 
                        key={price.id}
                        sx={{
                          bgcolor: isToday ? 'action.selected' : isPast ? 'action.hover' : 'inherit',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {formatDateOnlyForDisplay(price.date)}
                            {isPast && (
                              <Typography variant="caption" color="text.secondary">
                                (Past)
                              </Typography>
                            )}
                            {isToday && (
                              <Typography variant="caption" color="primary.main" fontWeight="bold">
                                (Today)
                              </Typography>
                            )}
                            {isFuture && (
                              <Typography variant="caption" color="success.main">
                                (Future)
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>${price.diesel_price_per_liter}</TableCell>
                        <TableCell>${price.def_price_per_liter}</TableCell>
                        <TableCell>${price.dyed_price_per_liter ?? '0'}</TableCell>
                        <TableCell>{price.created_by_name || 'N/A'}</TableCell>
                        <TableCell>
                          {isPast ? (
                            <Typography variant="caption" color="text.secondary">
                              Past
                            </Typography>
                          ) : isToday ? (
                            <Typography variant="caption" color="primary.main" fontWeight="bold">
                              Current
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="success.main">
                              Future
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPricingDialog(price)}
                            color="primary"
                            title={isPast ? 'Edit past pricing' : 'Edit pricing'}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Box>

        {/* Pricing Dialog */}
        <Dialog open={pricingDialogOpen} onClose={handleClosePricingDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {pricingFormData.id ? 'Edit Pricing' : 'Set Pricing'}
          </DialogTitle>
          <DialogContent>
            {pricingError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPricingError('')}>
                {pricingError}
              </Alert>
            )}
            {pricingSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPricingSuccess('')}>
                {pricingSuccess}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="date"
                label="Date"
                type="date"
                value={pricingFormData.date}
                onChange={handlePricingChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={pricingFormData.id ? 'You can change the date or update prices for this date' : 'Select the date for this pricing'}
              />
              <TextField
                name="diesel_price_per_liter"
                label="Diesel price per gallon ($)"
                type="number"
                value={pricingFormData.diesel_price_per_liter}
                onChange={handlePricingChange}
                required
                fullWidth
                inputProps={{ step: '0.0001', min: '0' }}
                helperText="Up to 4 decimal places"
              />
              <TextField
                name="def_price_per_liter"
                label="DEF price per gallon ($)"
                type="number"
                value={pricingFormData.def_price_per_liter}
                onChange={handlePricingChange}
                required
                fullWidth
                inputProps={{ step: '0.0001', min: '0' }}
                helperText="Up to 4 decimal places"
              />
              <TextField
                name="dyed_price_per_liter"
                label="Dyed price per gallon ($)"
                type="number"
                value={pricingFormData.dyed_price_per_liter}
                onChange={handlePricingChange}
                required
                fullWidth
                inputProps={{ step: '0.0001', min: '0' }}
                helperText="Up to 4 decimal places (0 if not offered)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePricingDialog}>Cancel</Button>
            <Button onClick={handlePricingSubmit} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Layout>
  );
};

export default ManagerDashboard;



